import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  flowMax,
} from 'ad-hok'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { addNavigate } from 'shared/src/utils/addNavigate'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { sansSerifFontFamily, serifFontFamily } from 'utils/styles'
import backButton from '../../assets/back-button-auth.svg'
import { TextField } from '../TextField'
import { EmailVerification } from './EmailVerification'
import { TextButton } from './TextButton'

const useStyles = createUseStyles({
  backButton: {
    background: 'none',
    border: 'none',
    padding: 0,
  },
  title: {
    color: '#ffffff',
    fontFamily: serifFontFamily,
    fontSize: 32,
    fontWeight: 600,
    lineHeight: '140%',
    letterSpacing: 0.01,
    marginTop: 16,
    marginBottom: 12,
    fontStyle: 'italic',
  },
  subtitle: {
    fontFamily: sansSerifFontFamily,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '187%',
    color: 'rgba(255, 255, 255, 0.56)',
    marginLeft: 30,
    marginBottom: 40,
  },
  passwordRequirements: {
    fontFamily: sansSerifFontFamily,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '160%',
    letterSpacing: 0.2,
    marginBottom: 18,
    color: 'rgba(54, 52, 50, 0.8)',
  },
  passwordRequirementsError: {
    color: '#F57561',
  },
})

interface Props {
  onBack: () => void
  email?: string
}

export const RecoverPassword: FC<Props> = flowMax(
  addDisplayName('RecoverPassword'),
  addState('showEmailMessage', 'setShowEmailMessage', false),
  addState('email', 'onChangeEmail', ({ email }) => email ?? ''),
  addNavigate,
  addApi,
  addHandlers({
    onClick:
      ({ api, email, setShowEmailMessage }) =>
      () => {
        if (email) {
          api.auth.resetPasswordEmail({ email, source: 'shopperWeb' })
          setShowEmailMessage(true)
        }
      },
  }),
  addHandlers({
    onKeyPress:
      ({ onClick, email }) =>
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && email) {
          onClick()
        }
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({
    email,
    onChangeEmail,
    onBack,
    onClick,
    onKeyPress,
    showEmailMessage,
    classes,
    t,
  }) => (
    <div>
      {!showEmailMessage && (
        <button onClick={onBack} className={classes.backButton}>
          <img src={backButton} alt={t('back')} />
        </button>
      )}
      {showEmailMessage && email ? (
        <EmailVerification
          email={email}
          emailType="passwordReset"
          onBack={onBack}
        />
      ) : (
        <>
          <h1 className={classes.title}>{t('auth.recoverPassword.title')}</h1>
          <p className={classes.subtitle}>
            {t('auth.recoverPassword.subtitle')}
          </p>
          <TextField
            id="email"
            label={t('auth.fields.email')}
            value={email ?? ''}
            onChange={(email) => onChangeEmail(email)}
            onKeyPress={onKeyPress}
          />
          <TextButton
            text={t('auth.recoverPassword.button')}
            onClick={onClick}
            disabled={!email}
          />
        </>
      )}
    </div>
  )
)
